import React, { useEffect, useState } from 'react';
import ToastNotification from './toast-notification';
import {
  onForegroundMessage,
  ensurePushToken,
  pushPermission,
} from '../services/firebasePush';
import { logger } from '../utils/logger';

/** Whether a validated user session is cached (set by App /auth/me + login flows). */
function isLoggedIn(): boolean {
  return !!localStorage.getItem('srs_user');
}

/**
 * App-wide push bootstrap + foreground handler for the website (tenants).
 *
 * When logged in and permission is already granted, silently refreshes the FCM
 * token. Does not auto-prompt — users enable push from the Notifications page.
 * Foreground messages surface as a toast and emit `fcm-message` for the bell.
 */
export default function PushManager() {
  const [toast, setToast] = useState<{ message: string } | null>(null);
  const [authed, setAuthed] = useState<boolean>(isLoggedIn);

  useEffect(() => {
    const sync = () => setAuthed(isLoggedIn());
    window.addEventListener('userUpdated', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('userUpdated', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    if (!authed) return;

    let active = true;
    let unsubscribe = () => {};

    (async () => {
      try {
        const perm = pushPermission();
        if (perm === 'unsupported' || perm === 'denied') return;

        if (perm === 'granted') {
          const res = await ensurePushToken();
          if (!res.ok) {
            logger.warn('Push token refresh did not complete', res.reason);
            return;
          }
        } else {
          return;
        }

        const fn = await onForegroundMessage((payload) => {
          const title = payload.notification?.title || (payload.data as any)?.title || 'Notification';
          const body = payload.notification?.body || (payload.data as any)?.body || '';
          setToast({ message: body ? `${title}: ${body}` : title });
          window.dispatchEvent(new CustomEvent('fcm-message', { detail: payload }));
        });

        if (active) unsubscribe = fn;
        else fn();
      } catch (err) {
        logger.error('PushManager bootstrap failed', err);
      }
    })();

    return () => {
      active = false;
      unsubscribe();
    };
  }, [authed]);

  if (!toast) return null;
  return (
    <ToastNotification
      message={toast.message}
      type="success"
      onClose={() => setToast(null)}
    />
  );
}
